import { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import { 
    Shield, 
    Lock, 
    Users, 
    BarChart3, 
    Settings, 
    LogOut, 
    CheckCircle2, 
    AlertTriangle,
    Database,
    ExternalLink,
    Zap,
    ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';

/**
 * AdminDashboard - Cresta Master Edition
 * 
 * High-privilege dashboard for platform-wide management.
 * Designed with a refined, high-security luxury aesthetic.
 */
function AdminDashboard() {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [stats, setStats] = useState({ totalSalons: 0, activeUsers: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [activeView, setActiveView] = useState<'dashboard' | 'users' | 'audit' | 'roles'>('dashboard');
    
    // 2FA Setup State
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [showSetup, setShowSetup] = useState(false);
    const [setupData, setSetupData] = useState<{ secret: string; qrCode: string } | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [isActivating, setIsActivating] = useState(false);

    const [auditLogs, setAuditLogs] = useState<any[]>([]);

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            // Prioritize the new twoFactorEnabled field, fallback to legacy
            setIs2FAEnabled(user.twoFactorEnabled !== undefined ? user.twoFactorEnabled : user.is2FAEnabled);
        }
        fetchStats();
    }, []);

    const fetchAuditLogs = async () => {
        try {
            const data = await adminApi.getAuditLogs();
            setAuditLogs(data);
            setActiveView('audit');
        } catch (error) {
            addToast('error', 'Error al cargar logs de auditoría');
        }
    };

    const fetchStats = async () => {
        try {
            const data = await adminApi.getStats();
            setStats(data);
        } catch (error) {
            console.warn('Failed to fetch stats', error);
        }
    };

    const handleStartSetup = async () => {
        setIsLoading(true);
        try {
            const data = await adminApi.setupTwoFactor();
            setSetupData(data);
            setShowSetup(true);
        } catch (error) {
            addToast('error', 'Error al iniciar configuración 2FA');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyAndEnable = async () => {
        if (verificationCode.length !== 6) return;
        
        setIsActivating(true);
        try {
            await adminApi.enableTwoFactor(setupData!.secret, verificationCode);
            
            // Update local storage
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                user.twoFactorEnabled = true;
                localStorage.setItem('user', JSON.stringify(user));
            }
            
            setIs2FAEnabled(true);
            setShowSetup(false);
            addToast('success', 'Doble factor de autenticación activado con éxito');
        } catch (error) {
            addToast('error', 'Código inválido. Intenta de nuevo.');
        } finally {
            setIsActivating(false);
        }
    };

    const handleDisable2FA = async () => {
        if (!window.confirm('¿Estás seguro de desactivar el 2FA? Esto reducirá la seguridad de tu cuenta.')) return;
        
        try {
            await adminApi.disableTwoFactor();
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                user.twoFactorEnabled = false;
                localStorage.setItem('user', JSON.stringify(user));
            }
            setIs2FAEnabled(false);
            addToast('info', 'Doble factor desactivado');
        } catch (error) {
            addToast('error', 'Error al desactivar 2FA');
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white p-6 md:p-12 font-sans selection:bg-teal-500/30">
            {/* Background Texture & Glow */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-30">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-teal-900/20 rounded-full blur-[120px]"></div>
                <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] bg-blue-900/10 rounded-full blur-[100px]"></div>
            </div>

            {/* Sidebar/Nav Mock (simplified for MVP) */}
            <div className="max-w-7xl mx-auto flex flex-col gap-10 relative z-10">
                
                {/* Header */}
                <header className="flex justify-between items-center bg-white/5 backdrop-blur-md p-6 rounded-3xl border border-white/10 fade-in-down">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-500/20">
                            <Shield className="text-white" size={24} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">Cresta <span className="text-teal-400">Master</span></h1>
                            <p className="text-xs text-gray-500 uppercase tracking-[0.2em]">Global Management Panel</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="hidden md:flex flex-col items-end mr-4">
                            <span className="text-sm font-medium">System Administrator</span>
                            <span className="text-[10px] text-teal-500 font-mono">STATUS: OPTIMIZED</span>
                        </div>
                        <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={handleLogout}>
                            <LogOut size={20} />
                        </Button>
                    </div>
                </header>

                {activeView === 'users' ? (
                    <UserManagement onBack={() => setActiveView('dashboard')} />
                ) : activeView === 'audit' ? (
                    <div className="space-y-6">
                        <button 
                            onClick={() => setActiveView('dashboard')}
                            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={20} />
                            <span>Volver</span>
                        </button>
                        <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
                            <table className="w-full text-left">
                                <thead className="bg-white/10 uppercase text-[10px] font-bold text-gray-400">
                                    <tr>
                                        <th className="p-6">Fecha</th>
                                        <th className="p-6">Acción</th>
                                        <th className="p-6">Admin</th>
                                        <th className="p-6">Target</th>
                                        <th className="p-6">Detalles</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {auditLogs.map((log, i) => (
                                        <tr key={i} className="hover:bg-white/[0.02] text-sm">
                                            <td className="p-6 text-gray-400">{new Date(log.timestamp).toLocaleString()}</td>
                                            <td className="p-6 font-bold text-teal-400">{log.action}</td>
                                            <td className="p-6">{log.actorEmail}</td>
                                            <td className="p-6 text-xs">{log.targetType}: {log.targetId}</td>
                                            <td className="p-6 text-xs text-gray-500">{log.details}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : activeView === 'roles' ? (
                    <RoleManagement onBack={() => setActiveView('dashboard')} />
                ) : (
                    <main className="grid grid-cols-1 lg:grid-cols-3 gap-8 fade-in">
                    
                    {/* Left Column: Stats & Monitoring */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Stat Card 1 */}
                            <div className="bg-white/5 border border-white/10 p-8 rounded-[32px] hover-lift transition-all duration-500 group relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                    <BarChart3 size={120} />
                                </div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                                        <Database size={20} />
                                    </div>
                                    <h3 className="text-sm text-gray-400 font-medium">Salones Registrados</h3>
                                </div>
                                <div className="flex items-end gap-3">
                                    <span className="text-5xl font-bold leading-none">{stats.totalSalons}</span>
                                    <span className="text-emerald-500 text-sm font-medium mb-1">+12% vs last month</span>
                                </div>
                            </div>

                            {/* Stat Card 2 */}
                            <div className="bg-white/5 border border-white/10 p-8 rounded-[32px] hover-lift transition-all duration-500 group relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                    <Users size={120} />
                                </div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                                        <Zap size={20} />
                                    </div>
                                    <h3 className="text-sm text-gray-400 font-medium">Usuarios Activos</h3>
                                </div>
                                <div className="flex items-end gap-3">
                                    <span className="text-5xl font-bold leading-none">{stats.activeUsers}</span>
                                    <span className="text-blue-500 text-sm font-medium mb-1">Live Monitor</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions Grid */}
                        <div className="bg-white/5 border border-white/10 p-8 rounded-[32px]">
                            <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
                                <Settings size={22} className="text-gray-400" />
                                Gestión Rápida
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                    { label: 'Usuarios y Soporte', icon: <Users size={20} />, active: true, onClick: () => setActiveView('users') },
                                    { label: 'Roles y Permisos', icon: <Shield size={20} />, active: true, onClick: () => setActiveView('roles') },
                                    { label: 'Auditoría Global', icon: <Database size={20} />, active: true, onClick: fetchAuditLogs },
                                    { label: 'Infraestructura', icon: <Settings size={20} />, active: false },
                                ].map((action, i) => (
                                    <button 
                                        key={i}
                                        disabled={!action.active}
                                        onClick={action.onClick}
                                        className={`flex flex-col items-center justify-center gap-4 p-6 rounded-2xl border transition-all duration-300 ${
                                            action.active 
                                            ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' 
                                            : 'bg-white/[0.02] border-white/5 opacity-50 cursor-not-allowed'
                                        }`}
                                    >
                                        {action.icon}
                                        <span className="text-xs font-bold uppercase tracking-wider">{action.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Security Center */}
                    <div className="space-y-8">
                        <section className="bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 p-8 rounded-[32px] relative overflow-hidden">
                             {/* Security Glow */}
                            <div className={`absolute -top-10 -right-10 w-32 h-32 blur-[60px] opacity-20 ${is2FAEnabled ? 'bg-emerald-500' : 'bg-red-500'}`}></div>

                            <div className="flex items-center gap-3 mb-8">
                                <Lock className={`${is2FAEnabled ? 'text-emerald-400' : 'text-red-400'}`} size={24} />
                                <h2 className="text-xl font-bold">Account Security</h2>
                            </div>

                            {!showSetup ? (
                                <div className="space-y-6">
                                    <div className="p-4 rounded-2xl bg-white/5 border border-white/5 flex items-start gap-3">
                                        {is2FAEnabled ? (
                                            <CheckCircle2 className="text-emerald-500 mt-1 shrink-0" size={18} />
                                        ) : (
                                            <AlertTriangle className="text-red-500 mt-1 shrink-0" size={18} />
                                        )}
                                        <div className="space-y-1">
                                            <p className="font-semibold">{is2FAEnabled ? '2FA Active' : '2FA Inactive'}</p>
                                            <p className="text-xs text-gray-500 leading-relaxed">
                                                {is2FAEnabled 
                                                    ? 'Su cuenta está protegida con autenticación de doble factor.' 
                                                    : 'Active el 2FA para proteger su acceso con un código dinámico.'}
                                            </p>
                                        </div>
                                    </div>

                                    {is2FAEnabled ? (
                                        <Button variant="ghost" fullWidth className="text-red-400 border border-red-400/20" onClick={handleDisable2FA}>
                                            Desactivar 2FA
                                        </Button>
                                    ) : (
                                        <Button 
                                            fullWidth 
                                            className="bg-teal-500 hover:bg-teal-600 text-black font-bold h-14" 
                                            onClick={handleStartSetup}
                                            isLoading={isLoading}
                                        >
                                            Configurar 2FA
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                                    <div className="text-center space-y-2">
                                        <p className="text-sm font-medium text-teal-400">Escanea con Google Authenticator</p>
                                        <p className="text-[10px] text-gray-500">También compatible con Authy y Microsoft Authenticator</p>
                                    </div>

                                    <div className="bg-white p-4 rounded-2xl flex items-center justify-center shadow-2xl shadow-white/5">
                                        <img src={setupData?.qrCode} alt="2FA QR Code" className="w-[180px] h-[180px]" />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <p className="text-xs text-gray-400 text-center uppercase tracking-wider">Verification Code</p>
                                        <div className="flex gap-2">
                                            <Input 
                                                className="bg-white/5 border-white/10 text-center text-xl font-mono h-14"
                                                placeholder="000 000"
                                                value={verificationCode}
                                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0,6))}
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <Button 
                                            className="flex-1 bg-white text-black font-bold h-12" 
                                            onClick={handleVerifyAndEnable}
                                            isLoading={isActivating}
                                        >
                                            Confirmar
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            className="text-gray-400" 
                                            onClick={() => setShowSetup(false)}
                                        >
                                            Cancelar
                                        </Button>
                                    </div>
                                    
                                    <div className="text-[10px] text-gray-500 font-mono text-center">
                                        SECRET: {setupData?.secret}
                                    </div>
                                </div>
                            )}

                            <div className="mt-10 pt-8 border-t border-white/5 space-y-4">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Whitelisting Status</span>
                                    <span className="text-emerald-400 font-bold">ACTIVE</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Last Login Audit</span>
                                    <button 
                                        onClick={fetchAuditLogs}
                                        className="text-blue-500 flex items-center gap-1 hover:underline"
                                    >
                                        View Logs <ExternalLink size={10} />
                                    </button>
                                </div>
                            </div>
                        </section>

                        <div className="p-8 rounded-[32px] bg-red-950/20 border border-red-500/20">
                            <h3 className="text-red-500 font-bold mb-2 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Master Override
                            </h3>
                            <p className="text-[10px] text-red-500/70 leading-relaxed uppercase">
                                Use caution. Actions here bypass normal validation and are logged immediately for compliance.
                            </p>
                        </div>
                    </div>
                    </main>
                )}
            </div>
        </div>
    );
}

export default AdminDashboard;
